import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IUser } from 'interfaces/user';
import { initTokenRefresh, setAuthorizationToken } from 'services';
import { login as loginService, ILoginPayload } from 'services/auth';
import { getProfileData } from 'services/user';
import { RootState } from 'store';
import Fingerprint from 'utils/fingerprint';
import { formateUserResponseData } from 'utils/formatter';
import { setToken } from 'utils/token';

interface UserState {
	user: null | IUser;
	isAuth: boolean;
};

const initialState: UserState = {
	user: null,
	isAuth: false,
};

export const login = createAsyncThunk(
	'user/login',
	async (payload: ILoginPayload) => {
		const fingerprint = await Fingerprint.get();
		const { data: token } = await loginService({ ...payload, fingerprint });
		setToken(token);
		setAuthorizationToken(token);
		initTokenRefresh(false);
		return token;
	}
);

export const fetchProfileData = createAsyncThunk(
	'user/fetchProfileData',
	async () => {
		const profile = await getProfileData();
		const user = formateUserResponseData(profile.data);
		return { user };
	}
);

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		setAuth: (state, action: PayloadAction<boolean>) => {
			state.isAuth = action.payload
		},
		setUser: (state, action: PayloadAction<IUser>) => {
			state.user = action.payload
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(login.fulfilled, (state) => {
				state.isAuth = true;
			})
			.addCase(login.rejected, (state) => {
				state.isAuth = false;
			})
			.addCase(fetchProfileData.fulfilled, (state, action) => {
				const { user } = action.payload;
				state.user = user;
			})
	},
});

export const { setAuth, setUser } = userSlice.actions;

export const selectIsAuth = (state: RootState) => state.user.isAuth;
export const selectUser = (state: RootState) => state.user.user;

export default userSlice.reducer;
